import React from 'react';
import { Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useDraggable } from '@dnd-kit/core';

interface DraggableGameCardProps {
  gameId: number;
  disabled?: boolean;          // 公開閲覧時・プライオリティモード時はドラッグ無効
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (gameId: number) => void;
  // ドラッグ時に一緒に動かすID群（選択中のカードをドラッグした場合）
  dragGameIds?: number[];
  children: React.ReactNode;
}

// ライブラリのゲームカードを @dnd-kit のドラッグ元にするラッパー。
// GameCard 本体には手を入れず、選択モードのオーバーレイもここで重ねる。
const DraggableGameCard: React.FC<DraggableGameCardProps> = ({
  gameId,
  disabled,
  selectionMode,
  selected,
  onToggleSelect,
  dragGameIds,
  children,
}) => {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `game-${gameId}`,
    disabled,
    data: {
      type: 'game',
      gameId,
      gameIds: dragGameIds && dragGameIds.length > 0 ? dragGameIds : [gameId],
    },
  });

  // ダイアログ・メニュー等は Portal で body 直下に描画されるが、React の合成イベントは
  // DOM ツリーではなく React ツリーを辿って伝播するため、その pointer 操作がこの Box の
  // dnd-kit リスナーに届き、裏のカードのドラッグが誤発火してしまう。
  // 「カードの DOM 内（currentTarget の子孫）から発生した操作」以外はドラッグを開始しない。
  const guardedListeners = React.useMemo(() => {
    if (!listeners) return listeners;
    const guarded: Record<string, (e: any) => void> = {};
    for (const [name, handler] of Object.entries(listeners)) {
      guarded[name] = (e: any) => {
        if (e?.target instanceof Node && e?.currentTarget instanceof Node
            && !e.currentTarget.contains(e.target)) {
          return;
        }
        (handler as (e: any) => void)?.(e);
      };
    }
    return guarded;
  }, [listeners]);

  return (
    <Box
      ref={setNodeRef}
      {...guardedListeners}
      {...attributes}
      sx={{
        position: 'relative',
        height: '100%',
        outline: 'none',
        touchAction: 'manipulation',
        opacity: isDragging ? 0.35 : 1,
        transition: 'opacity .12s ease, box-shadow .12s ease',
        cursor: disabled ? 'default' : isDragging ? 'grabbing' : 'grab',
        borderRadius: '6px',
        ...(selectionMode && selected && { boxShadow: '0 0 0 2px #d9ff00' }),
      }}
    >
      {children}

      {selectionMode && (
        <Box
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleSelect?.(gameId);
          }}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            borderRadius: '6px',
            cursor: 'pointer',
            backgroundColor: selected ? 'rgba(217,255,0,0.12)' : 'rgba(0,0,0,0.28)',
            transition: 'background-color .12s ease',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-end',
          }}
        >
          <Box sx={{ m: 0.5, lineHeight: 0, borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.65)' }}>
            {selected ? (
              <CheckCircleIcon sx={{ fontSize: '1.4rem', color: '#d9ff00', display: 'block' }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ fontSize: '1.4rem', color: 'rgba(255,255,255,0.85)', display: 'block' }} />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DraggableGameCard;
